/* eslint-disable i18next/no-literal-string */
import { cn } from "~/lib/utils";
import VehicleMarker from "./VehicleMarker";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

const phone = imageUrl("/images/landing/geo-zones-phone.webp");

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const zone = imageUrl("/images/landing/geo-zones-zone.png");
const vehicle = imageUrl("/images/landing/geo-zones-vehicle.png");
const notification = imageUrl("/images/landing/geo-zones-notification.png");

/**
 * Ported from bouncieweb's `LandingPageComponents/HighlightAnimations/GeoZonesAnimation.js`.
 *
 * A phone showing a map: the whole scene slides in, a geo-zone circle fades in,
 * a vehicle pin + puck slide together from off-map into the zone, and a push
 * notification eases in from the top-left. Only the non-fleet variant is ported
 * (none of the ported landing pages set `isFleet`).
 *
 * The pin and the marker are kept as non-siblings (per the original z-index
 * note) but move in sync — their translate offsets are computed so the pixel
 * distances match despite different element sizes. All keyframes run once when
 * `animate` becomes true (parent's `useHasBeenOnScreen`).
 */

// Original sync math: marker 9% box offset (100%,31%); pin 15% box gets a
// proportionally larger % offset so both travel the same on-screen distance.
const markerSize = 9;
const markerOffsetX = 100;
const markerOffsetY = 31;
const pinSize = 15;
const pinOffsetX = markerOffsetX * (markerSize / pinSize);
const pinOffsetY = markerOffsetY * (markerSize / pinSize);

const common = {
  animationTimingFunction: "ease-out",
  animationFillMode: "forwards" as const,
  animationIterationCount: 1,
};

export default function GeoZonesAnimation({
  animate,
  className,
}: {
  animate: boolean;
  /**
   * Appended to the wrapper so a page can nudge the animation's `top`/`right`
   * (the original did this by extending the styled component — see the Turo
   * grid's `GeoZonesAnimationCustom`).
   */
  className?: string;
}) {
  return (
    <>
      <style>{`
        @keyframes cf-geo-wrapper {
          0%   { transform: translate(-35px, 0px); }
          100% { transform: translate(0px, 0px); }
        }
        @keyframes cf-geo-zone {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes cf-geo-marker {
          0%   { transform: translate(${markerOffsetX}%, ${markerOffsetY}%); }
          100% { transform: translate(0px, 0px); }
        }
        @keyframes cf-geo-pin {
          0%   { transform: translate(${pinOffsetX}%, ${pinOffsetY}%); }
          100% { transform: translate(0px, 0px); }
        }
        @keyframes cf-geo-notification {
          0%   { transform: translate(15%, 50%); }
          100% { transform: translate(0px, 0px); }
        }
      `}</style>

      {/* GeoZonesAnimationWrapper */}
      <div
        className={cn(
          "pointer-events-none absolute",
          "top-[6rem] right-[-23%] w-[140%]",
          "min-[431px]:right-[3%] min-[431px]:w-[110%]",
          "sm:right-0",
          "md:top-[2rem] md:right-[2rem] md:w-[29rem]",
          "lg:top-[0.75rem] lg:right-[3rem] lg:w-[35rem]",
          "xl:right-[2.5rem] xl:w-[41rem]",
          className,
        )}
        style={{
          aspectRatio: "1 / 1",
          ...common,
          animationName: animate ? "cf-geo-wrapper" : undefined,
          animationDuration: animate ? "6.5s" : undefined,
        }}
      >
        {/* Notification */}
        <Picture
          src={notification}
          alt=""
          className="absolute h-fit max-w-none"
          style={{
            width: "30%",
            top: "2%",
            left: "15%",
            transform: "translate(15%, 50%)",
            ...common,
            animationTimingFunction: "cubic-bezier(0,0,.2,1)",
            animationName: animate ? "cf-geo-notification" : undefined,
            animationDuration: animate ? "1.5s" : undefined,
            animationDelay: animate ? "1.3s" : undefined,
          }}
        />

        {/* Phone */}
        <Picture
          src={phone}
          alt=""
          className="absolute h-fit w-full max-w-none"
        />

        {/* VehicleMarkerPositioner */}
        <div
          className="absolute"
          style={{
            top: "20.4%",
            left: "54%",
            width: `${markerSize}%`,
            height: `${markerSize}%`,
            transform: `translate(${markerOffsetX}%, ${markerOffsetY}%)`,
            ...common,
            animationName: animate ? "cf-geo-marker" : undefined,
            animationDuration: animate ? "6.5s" : undefined,
          }}
        >
          <VehicleMarker
            size="100%"
            rotate="rotateY(25deg) rotateX(-45deg) rotateZ(-72deg)"
          />
        </div>

        {/* Zone */}
        <Picture
          src={zone}
          alt=""
          className="absolute h-fit max-w-none"
          style={{
            top: "12.2%",
            left: "21.7%",
            width: "42%",
            opacity: 0,
            ...common,
            animationName: animate ? "cf-geo-zone" : undefined,
            animationDuration: animate ? "0.5s" : undefined,
            animationDelay: animate ? "1.5s" : undefined,
          }}
        />

        {/* VehiclePin */}
        <Picture
          src={vehicle}
          alt=""
          className="absolute h-fit max-w-none"
          style={{
            top: "11%",
            left: "54.5%",
            width: `${pinSize}%`,
            height: `${pinSize}%`,
            transform: `translate(${pinOffsetX}%, ${pinOffsetY}%)`,
            ...common,
            animationName: animate ? "cf-geo-pin" : undefined,
            animationDuration: animate ? "6.5s" : undefined,
          }}
        />
      </div>
    </>
  );
}
